import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wand2, Loader2, ShieldAlert, Edit3, GitBranch, Anchor, Eye, Swords, Cpu } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { HudButton } from '../../hud';
import DeterministicWhiteboxButton from '../DeterministicWhiteboxButton';
import { PromptPreviewDialog } from '../ProjectGenerationLauncher';
import { API, STAGES, ENGINES } from './constants';

const opts = { withCredentials: true };
const MOMENTS = ['Matin', 'Midi', 'Midi (sans ombre portée)', 'Golden Hour', 'Nuit'];

const ModBtn = ({ icon: Icon, label, hint, onClick, disabled, testid }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={hint}
          className="flex-1 min-w-[150px] text-left rounded-lg px-3 py-2 transition-colors disabled:opacity-35 disabled:cursor-not-allowed hover:border-[#D9A05B]"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--c-border)' }} data-testid={testid}>
    <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold" style={{ color: 'var(--c-text)' }}><Icon className="w-3.5 h-3.5" style={{ color: 'var(--c-gold)' }} />{label}</span>
    <p className="text-[10px] mt-0.5" style={{ color: 'var(--c-text-faint)' }}>{hint}</p>
  </button>
);

/** V15.21 — Zone 2c : un CTA « Générer » évident + rangée « Modifier » explicite. */
export const StepActionLauncher = ({ projectId, lotId, lot, stage, data, values, lotJobs, onLaunched, onOpenAnnotation, onOpenVariation, onChangeReference }) => {
  const [launching, setLaunching] = useState(false);
  const [moment, setMoment] = useState('');
  // V15.35 — étape 4 sécurisée : fond blanc verrouillé par défaut, abords et filigrane explicites.
  const [allowSurroundings, setAllowSurroundings] = useState(false);
  const [watermark, setWatermark] = useState(true);
  const [engine, setEngine] = useState(() => { try { return ENGINES.some((e) => e.id === localStorage.getItem(`pe_engine_${projectId}`)) ? localStorage.getItem(`pe_engine_${projectId}`) : 'nano_banana'; } catch { return 'nano_banana'; } });
  const pickEngine = (id) => { setEngine(id); try { localStorage.setItem(`pe_engine_${projectId}`, id); } catch { /* no-op */ } };
  const [preview, setPreview] = useState({ open: false, loading: false, data: null });
  const e = data?.stages?.[stage] || {};
  const meta = STAGES.find((s) => s.id === stage);
  const baseReady = !e.base || e.base.ready;
  const targetId = e.reference?.job_id || e.latest?.find((j) => j.status === 'completed')?.job_id || null;
  const target = lotJobs?.find((j) => j.job_id === targetId) || null;
  const issues = target?.fidelity_report?.issues || [];
  const hasRender = (e.counts?.completed || 0) > 0;

  const buildParams = (eng = engine, extra = {}) => ({ ...(values || {}), render_stage: stage, engine: eng, ...extra,
    ...(stage === 'final' ? { allow_surroundings: allowSurroundings, watermark, ...((moment || values?.moment_journee) ? { moment_journee: moment || values.moment_journee } : {}) } : {}) });

  // V15.27 — Duel : même référence, deux moteurs, deux rendus à comparer (étape 2 uniquement).
  const duel = async () => {
    if (!lot?.selected_plan_file_id) return toast.error('Ce lot n\'a pas de plan retenu.');
    setLaunching(true);
    const duelId = `duel_${Math.random().toString(16).slice(2, 10)}`;
    const tid = toast.loading('Duel Nano Banana Pro ⚔ GPT Image 1 — lancement des 2 rendus…');
    try {
      const results = await Promise.all(ENGINES.map((e) => axios.post(`${API}/projects/${projectId}/generation/create`,
        { lot_id: lotId, plan_file_id: lot.selected_plan_file_id, params: buildParams(e.id, { duel_id: duelId }), priority: 0 }, { ...opts, timeout: 45000 })));
      toast.success('Duel lancé (2 crédits)', { id: tid, description: 'Les deux rendus apparaîtront dans l\'historique avec le badge « Duel » et leur score du juge : ancrez le gagnant.' });
      results.forEach((r) => onLaunched?.(r.data));
    } catch (err) {
      toast.error('Duel impossible', { id: tid, description: err?.response?.data?.detail || err?.message });
    } finally { setLaunching(false); }
  };

  const generate = async () => {
    if (!lot?.selected_plan_file_id) return toast.error('Ce lot n\'a pas de plan retenu.');
    setLaunching(true);
    const tid = toast.loading(`Étape ${meta.n} · ${meta.label} — lancement…`);
    try {
      const { data: d } = await axios.post(`${API}/projects/${projectId}/generation/create`,
        { lot_id: lotId, plan_file_id: lot.selected_plan_file_id, params: buildParams(), priority: 0 }, { ...opts, timeout: 45000 });
      toast.success(`Étape ${meta.n} lancée`, { id: tid, description: `Le rendu apparaîtra dans l'historique du lot (~30-60 s). ID : ${d.job_id}` });
      onLaunched?.(d);
    } catch (err) {
      toast.error('Échec du lancement', { id: tid, description: err?.response?.data?.detail || err?.message });
    } finally { setLaunching(false); }
  };

  const correctFromJudge = async () => {
    if (!target) return;
    setLaunching(true);
    const tid = toast.loading('Correction d\'après le juge…');
    try {
      const note = [target.review_annotation, ...issues.map((i) => `Corriger : ${i}`)].filter(Boolean).join('\n');
      if (note) await axios.post(`${API}/projects/${projectId}/generations/${target.job_id}/review`, { annotation: note }, opts);
      const r = await axios.post(`${API}/projects/${projectId}/generations/${target.job_id}/relaunch`, {}, opts);
      toast.success('Rendu relancé avec les corrections du juge', { id: tid, description: 'L\'ancien rendu passe en Archives ; même étape, même référence.' });
      onLaunched?.(r.data);
    } catch (err) {
      toast.error('Relance impossible', { id: tid, description: err?.response?.data?.detail || err?.message });
    } finally { setLaunching(false); }
  };

  const openPreview = async () => {
    setPreview({ open: true, loading: true, data: null });
    try {
      const { data: d } = await axios.post(`${API}/projects/${projectId}/generation/preview-prompt`, { lot_id: lotId, params: buildParams() }, opts);
      setPreview({ open: true, loading: false, data: d });
    } catch (err) {
      toast.error('Prévisualisation impossible', { description: err?.response?.data?.detail || err?.message });
      setPreview({ open: false, loading: false, data: null });
    }
  };

  return (
    <div className="rounded-xl p-4 space-y-3" style={{ background: 'rgba(217,160,91,0.05)', border: '1px solid rgba(217,160,91,0.25)' }} data-testid="step-action-launcher">
      <div className="flex items-center gap-3 flex-wrap">
        {stage === 'whitebox' ? (
          <DeterministicWhiteboxButton projectId={projectId} lotId={lotId} vueAngle="Aérienne isométrique 45°" disabled={launching || !lot?.has_structure}
                                       onDone={(job) => onLaunched?.(job)} testId="cta-generate-step-primary" />
        ) : (
          <HudButton variant="primary" size="md" leftIcon={launching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                     onClick={generate} disabled={launching || !baseReady || !lot?.selected_plan_file_id} data-testid="cta-generate-step-primary"
                     title={baseReady ? `Lance l'étape ${meta.n} sur la référence ancrée (1 crédit)` : 'Ancrez d\'abord la référence de l\'étape précédente'}>
            {launching ? 'Lancement…' : stage === 'final' ? `Générer une variante ambiance` : `${hasRender ? 'Régénérer' : 'Générer'} l'étape ${meta.n} · ${meta.label}`}
            <span className="ml-1 text-[10px] font-normal opacity-70">· 1 crédit</span>
          </HudButton>
        )}
        {stage !== 'whitebox' && (
          <Select value={engine} onValueChange={pickEngine}>
            <SelectTrigger className="w-[200px] h-9 bg-white/[0.04] border-white/10 text-white text-[12px]" title="Moteur d'image utilisé pour cette étape" data-testid="step-engine-select">
              <Cpu className="w-3.5 h-3.5 mr-1.5 opacity-70" /><SelectValue>{ENGINES.find((e) => e.id === engine)?.label}</SelectValue>
            </SelectTrigger>
            <SelectContent>{ENGINES.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`step-engine-option-${e.id}`}><span className="flex flex-col"><span>{e.label}</span><span className="text-[10px] opacity-60">{e.vendor} — {e.hint}</span></span></SelectItem>)}</SelectContent>
          </Select>
        )}
        {stage === 'textured' && (
          <HudButton variant="ghost" size="md" leftIcon={<Swords className="w-3.5 h-3.5" />} onClick={duel} disabled={launching || !baseReady || !lot?.selected_plan_file_id} data-testid="step-duel-btn"
                     title="Lance l'étape 2 sur les deux moteurs à partir de la même référence (2 crédits) — ancrez le gagnant">Duel 2 moteurs</HudButton>
        )}
        {stage === 'final' && (
          <>
            <Select value={moment || values?.moment_journee || 'Golden Hour'} onValueChange={setMoment}>
              <SelectTrigger className="w-[210px] h-9 bg-white/[0.04] border-white/10 text-white text-[12px]" data-testid="step-moment-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MOMENTS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <label className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer select-none" style={{ color: allowSurroundings ? '#FDE68A' : 'var(--c-text-2)' }}
                   title="Par défaut, le fond reste blanc (aucun ciel ni paysage). Cochez pour autoriser l'IA à dessiner les abords d'après le contexte extérieur du projet.">
              <input type="checkbox" checked={allowSurroundings} onChange={(e) => setAllowSurroundings(e.target.checked)} data-testid="step-final-surroundings-checkbox" />
              Abords & paysage
            </label>
            <label className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer select-none" style={{ color: 'var(--c-text-2)' }}
                   title={values?.branding_texte ? `Filigrane « ${values.branding_texte} » en bas à droite` : 'Aucun texte de filigrane configuré (Paramètres du prompt)'}>
              <input type="checkbox" checked={watermark} onChange={(e) => setWatermark(e.target.checked)} data-testid="step-final-watermark-checkbox" />
              Filigrane
            </label>
          </>
        )}
        {stage !== 'whitebox' && (
          <HudButton variant="ghost" size="md" leftIcon={<Eye className="w-3.5 h-3.5" />} onClick={openPreview} disabled={!baseReady} data-testid="step-preview-prompt"
                     title="Voir le prompt exact et l'image envoyés à l'IA — 0 crédit">Prévisualiser le prompt</HudButton>
        )}
        {!baseReady && <span className="text-[11px]" style={{ color: '#FDE68A' }} data-testid="step-blocked-hint">Étape {meta.n - 1} sans référence ancrée → sélectionnez-en une dans l'historique (icône ancre).</span>}
      </div>
      <div>
        <span className="hud-eyebrow">Modifier un rendu existant {target ? <span className="font-mono normal-case tracking-normal" style={{ color: 'var(--c-text-faint)' }}>· cible : {e.reference?.job_id ? 'référence ancrée' : 'dernier rendu'} {target.job_id}</span> : <span className="normal-case tracking-normal" style={{ color: 'var(--c-text-faint)' }}>· aucun rendu à cette étape</span>}</span>
        <div className="mt-1.5 flex gap-2 flex-wrap">
          <ModBtn icon={ShieldAlert} label="Corriger d'après le juge" hint={issues.length ? `${issues.length} écart(s) détecté(s) → relance avec consignes` : 'Relance à l\'identique avec les écarts du juge en consigne'} onClick={correctFromJudge} disabled={!target || launching || stage === 'whitebox'} testid="btn-correct-from-judge" />
          <ModBtn icon={Edit3} label="Annotation libre" hint="Entourez une zone, décrivez le changement, relance ciblée" onClick={() => target && onOpenAnnotation?.(target)} disabled={!target || stage === 'whitebox'} testid="btn-free-annotation" />
          <ModBtn icon={GitBranch} label="Variation" hint="Déclinaison : saison, moment, style, palette…" onClick={() => target && onOpenVariation?.(target)} disabled={!target || stage === 'whitebox'} testid="btn-create-variation" />
          <ModBtn icon={Anchor} label="Changer la référence" hint="Choisir un autre rendu de cette étape comme base de la suivante" onClick={() => onChangeReference?.(stage)} disabled={!hasRender || stage === 'final'} testid="btn-change-reference" />
        </div>
      </div>
      <PromptPreviewDialog open={preview.open} onOpenChange={(o) => setPreview((p) => ({ ...p, open: o }))} loading={preview.loading} data={preview.data} />
    </div>
  );
};

export default StepActionLauncher;
